import { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Group, Panel, Separator } from 'react-resizable-panels';
import { InspectorProvider, useInspector } from '../../../contexts/InspectorContext';
import ContentPanel from '../../../components/shared/ContentPanel';
import useTopicGroups from './useTopicGroups';
import YourNotesTabs from './YourNotesTabs';
import YourNotesNode from './YourNotesNode/YourNotesNode';
import { loadLayout } from './yourNotesHelpers';
import { subscribe } from '../../../services/utils/pubsub';
import groupNodeEngine from './Engines/GroupNodeEngine';
import YourNotesGroupManager from './YourNotesGroupManager/YourNotesGroupManager';
import { ComponentEvents } from '../../../services/wss/events/ComponentEvents';
import AppContext from '../../../controllers/AppContext'; // ADDED
import { TabNames } from '../../../services/wss/events/TabNames'; // ADDED
import logService from '../../../services/wss/logService3';
import { EngineNames } from '../../../services/wss/events/EngineNames';

import './YourNotesTab.css';
import GroupSettingsDlg from './YourNotesSettings/GroupSettingsDlg';

const _name = 'YourNotesTab';

export default function YourNotesTab() {
    return (
        <InspectorProvider>
            <YourNotesTabInner />
        </InspectorProvider>
    );
}

function YourNotesTabInner() {
    const { setActiveGid } = useInspector();
    const { groups, activeGroup, selectGroup, loading } = useTopicGroups();
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [groupManagerOpen, setGroupManagerOpen] = useState(false);

    // Set active tab in AppContext so ContentPanel knows which tab is active // ADDED
    useEffect(() => {
        AppContext.setCurrentTab(TabNames.YourNotes);
    }, []);

    // Sync activeGid in context when activeGroup changes — used by ContentPanelMeta for POST gid
    useEffect(() => {
        setActiveGid(activeGroup?.rk ?? null);
    }, [activeGroup]);

    // Subscribe to engine entrySelected — drives ContentPanel
    useEffect(() => {
        setSelectedEntry(groupNodeEngine.getSelectedEntry());

        const unsub = subscribe(ComponentEvents.channel, (e) => {
            if (e.name === EngineNames.GroupNodeEngine && e.target === 'entrySelected') {
                setSelectedEntry(e.data);
            }

            if (e.name === EngineNames.GroupNodeEngine && e.target === 'nodesChanged') {
                setSelectedEntry(e.data?.selectedEntry ?? null); // CHANGED — always update on nodesChanged
            }
        });

        return () => unsub();
    }, []);

    const handleLayoutChange = (sizes) => {
        localStorage.setItem('your-focus-panels', JSON.stringify(sizes));
    };

    return (
        <Box className="your-focus-root">
            <YourNotesTabs
                groups={groups}
                activeGroup={activeGroup}
                onSelect={selectGroup}
                onSettings={() => setSettingsOpen(true)}
                onGroupManager={() => setGroupManagerOpen(true)}
            />

            <GroupSettingsDlg open={settingsOpen} group={activeGroup} onClose={() => setSettingsOpen(false)} />
            <YourNotesGroupManager open={groupManagerOpen} onClose={() => setGroupManagerOpen(false)} />

            <Group
                id="focus-panel-group"
                orientation="horizontal"
                defaultLayout={loadLayout()}
                onLayoutChange={handleLayoutChange}
                style={{ flex: 1, minHeight: 0, width: '100%' }}
            >
                <Panel id="focus-list-panel" defaultSize={30} minSize={20}>
                    <Box
                        className="focus-list-scroll"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            overflowY: 'auto',
                            paddingRight: '8px',
                            '&::-webkit-scrollbar': { width: '8px' },
                            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                            '&::-webkit-scrollbar-thumb': {
                                bgcolor: '#3d6a99',
                                borderRadius: '4px',
                                borderRight: '4px solid transparent',
                                backgroundClip: 'padding-box',
                            },
                        }}
                    >
                        {loading && <CircularProgress size={20} sx={{ m: 2, color: '#4cceac' }} />}
                        {!loading && activeGroup && <YourNotesNode />}
                    </Box>
                </Panel>

                <Separator className="j-splitter" />

                <Panel id="focus-content-panel" minSize={30}>
                    <ContentPanel entry={selectedEntry} />
                </Panel>
            </Group>
        </Box>
    );
}
